import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { Download, Trash2, Maximize2, Move, Type, Minus, BarChart2, MoreVertical, X, Palette, Plus, RotateCw, MousePointer2, Save, Image, FileText, ArrowLeft, LayoutDashboard, Filter, XCircle, Settings } from 'lucide-react';
import { Dashboard, LayoutItem, ChartConfig, Dataset, DataRelationship, WorkspaceProps } from '../types';
import { ChartRenderer } from './ChartRenderer';
import { Panel, Header, EditableField, DraggableSidebarItem } from './Shared';
import { processDataForChart, processDataForAdvancedChart } from '../services/dataService';
import { exportElementAsPNG, exportDataAsCSV } from '../utils/exportUtils';
import { TextWidget } from './TextWidget';
import { LineWidget } from './LineWidget';
import { InteractionProvider, useInteraction, useInteractionOptional } from '../contexts/InteractionContext';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

const downloadSVG = (svgElement: SVGSVGElement, fileName: string) => {
    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgElement);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const BASIC_COLORS = [
    { name: 'Red', value: '#ef4444' },
    { name: 'Green', value: '#10b981' },
    { name: 'Blue', value: '#3b82f6' },
    { name: 'Yellow', value: '#f59e0b' },
    { name: 'Purple', value: '#8b5cf6' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Black', value: '#000000' },
    { name: 'White', value: '#ffffff' },
];

const ChartWrapper: React.FC<{ chartId: string, allCharts: ChartConfig[], datasets: Dataset[], relationships: DataRelationship[] }> = ({ chartId, allCharts, datasets, relationships }) => {
    const chart = allCharts.find(c => c.id === chartId);
    const [originalData, setOriginalData] = useState<any[]>([]);
    const [error, setError] = useState<string | null>(null);
    const interaction = useInteractionOptional();

    // Load data once
    useEffect(() => {
        if (!chart) return;
        try {
            setError(null);
            const isAdvanced = ['globe', 'force-directed', '3d-bar', 'map', 'scientific', 'kpi-card'].includes(chart.type);
            const d = isAdvanced
                ? processDataForAdvancedChart(datasets, relationships, chart)
                : processDataForChart(datasets, relationships, chart);
            setOriginalData(d);
        } catch (err) {
            console.error("ChartWrapper error:", err);
            setError((err as Error).message);
        }
    }, [chart, datasets, relationships]);

    // Apply filters from interaction context
    const filteredData = useMemo(() => {
        if (!interaction || interaction.activeFilters.length === 0) return originalData;
        if (!chart) return originalData;

        // Don't filter the source chart
        const relevantFilters = interaction.activeFilters.filter(f => f.sourceChartId !== chartId);
        if (relevantFilters.length === 0) return originalData;

        // Apply filters to data
        return originalData.filter(row => {
            return relevantFilters.every(filter => {
                const value = row[filter.field] ?? row.name; // Check field or fallback to 'name' for category
                return filter.values.includes(value);
            });
        });
    }, [originalData, interaction?.activeFilters, chartId, chart]);

    // Determine if this chart is filtered
    const activeFilterForChart = useMemo(() => {
        if (!interaction) return null;
        return interaction.activeFilters.find(f => f.sourceChartId !== chartId) || null;
    }, [interaction?.activeFilters, chartId]);

    if (!chart) return <div className="flex items-center justify-center h-full text-red-400">Chart not found</div>;
    if (error) return <div className="flex items-center justify-center h-full text-red-400 text-xs p-2 text-center">{error}</div>;

    return (
        <div className="w-full h-full relative">
            {/* Filtered indicator */}
            {activeFilterForChart && (
                <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-center">
                    <div className="bg-neon-gold/20 text-neon-gold text-[10px] font-medium px-2 py-0.5 rounded-b-md border border-t-0 border-neon-gold/30 flex items-center gap-1">
                        <Filter className="w-2.5 h-2.5" />
                        Filtered by: {activeFilterForChart.field}
                    </div>
                </div>
            )}
            <ChartRenderer
                config={chart}
                data={filteredData}
                chartId={chartId}
                highlightState={interaction?.highlightState || null}
                onDataPointClick={interaction ? (field, value) => {
                    if (chart.interactionSettings?.enableFilterLinking !== false) {
                        interaction.addFilter({ sourceChartId: chartId, field, values: [value] });
                    }
                } : undefined}
                onDataPointHover={interaction ? (field, value) => {
                    if (chart.interactionSettings?.enableHighlightLinking !== false) {
                        interaction.setHighlight({ sourceChartId: chartId, field, values: [value] });
                    }
                } : undefined}
                onMouseLeave={interaction ? () => interaction.clearHighlight() : undefined}
            />
        </div>
    );
};

const DashboardWidget: React.FC<{
    item: LayoutItem,
    allCharts: ChartConfig[],
    datasets: Dataset[],
    relationships: DataRelationship[],
    onRemove: (id: string) => void,
    onUpdateStyle: (id: string, style: Partial<LayoutItem>) => void,
    isDraggable?: boolean
}> = ({ item, allCharts, datasets, relationships, onRemove, onUpdateStyle, isDraggable }) => {
    const widgetRef = useRef<HTMLDivElement>(null);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [showStyleMenu, setShowStyleMenu] = useState(false);
    const [draggingAnnotation, setDraggingAnnotation] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState(false);

    const handleExportPNG = async () => {
        if (widgetRef.current) {
            setIsExporting(true);
            try {
                await exportElementAsPNG(widgetRef.current, `widget-${item.i}`);
            } catch (err) {
                console.error(err);
                alert("Export failed");
            } finally {
                setIsExporting(false);
                setShowExportMenu(false);
            }
        }
    };

    const handleExportCSV = () => {
        if (item.type === 'chart' && item.chartId) {
            const chart = allCharts.find(c => c.id === item.chartId);
            if (chart) {
                try {
                    const isAdvanced = ['globe', 'force-directed', '3d-bar', 'map'].includes(chart.type);
                    const data = isAdvanced
                        ? processDataForAdvancedChart(datasets, relationships, chart)
                        : processDataForChart(datasets, relationships, chart);
                    exportDataAsCSV(data, `data-${chart.title || 'widget'}`);
                } catch (err) {
                    console.error("Failed to export CSV:", err);
                    alert("Failed to generate CSV data.");
                }
            }
        }
        setShowExportMenu(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // Handle color drop
        const colorData = e.dataTransfer.getData('color');
        if (colorData) {
            // For line widgets, update lineColor
            if (item.type === 'line') {
                onUpdateStyle(item.i, { lineColor: colorData });
            } else {
                // For other widgets, update background
                if (colorData.startsWith('#') || colorData.startsWith('rgb')) {
                    onUpdateStyle(item.i, { backgroundColor: colorData, backgroundGradient: undefined });
                } else {
                    onUpdateStyle(item.i, { backgroundGradient: colorData, backgroundColor: undefined });
                }
            }
            return;
        }

        // Handle text dock drop (existing functionality)
        const type = e.dataTransfer.getData('type');
        if (type === 'text-dock') {
            const rect = widgetRef.current?.getBoundingClientRect();
            if (rect) {
                const y = e.clientY - rect.top;
                const dockPosition = y < rect.height * 0.3 ? 'top' : 'overlay';
                const newAnnotation = {
                    id: `ann-${Date.now()}`,
                    type: 'text' as const,
                    content: 'New Text',
                    x: 50,
                    y: 20,
                    dockPosition: dockPosition as any,
                    style: { fontSize: '14px', color: '#fff' }
                };
                const annotations = item.annotations || [];
                onUpdateStyle(item.i, { annotations: [...annotations, newAnnotation] });
            }
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.types.includes('color') || e.dataTransfer.types.includes('text-dock')) {
            e.dataTransfer.dropEffect = 'copy';
        }
    };

    const handleAddAnnotation = (type: 'text' | 'button') => {
        const newAnnotation = {
            id: `ann-${Date.now()}`,
            type,
            content: type === 'text' ? 'New Text' : 'Click Me',
            x: 50,
            y: 50,
            style: {
                backgroundColor: type === 'button' ? '#d4af37' : 'transparent',
                color: type === 'button' ? '#000' : '#fff',
                padding: type === 'button' ? '4px 12px' : '0',
                borderRadius: '4px',
                fontSize: '14px'
            }
        };
        const annotations = item.annotations || [];
        onUpdateStyle(item.i, { annotations: [...annotations, newAnnotation] });
    };

    const handleAnnotationDragStart = (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        setDraggingAnnotation(id);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (draggingAnnotation && widgetRef.current) {
            const rect = widgetRef.current.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 100;
            const y = ((e.clientY - rect.top) / rect.height) * 100;

            const annotations = item.annotations || [];
            const updated = annotations.map(a => a.id === draggingAnnotation ? { ...a, x, y } : a);
            onUpdateStyle(item.i, { annotations: updated });
        }
    };

    const handleMouseUp = () => {
        setDraggingAnnotation(null);
    };

    // Special rendering for Divider Lines
    if (item.type === 'line') {
        return (
            <div
                ref={widgetRef}
                className={`flex items-center justify-center relative group rounded-2xl transition-all duration-300 border-2 border-transparent hover:border-neon-gold/30 ${item.lineOrientation === 'vertical' ? 'w-16 h-full mx-auto' : 'w-full h-16 my-auto'}`}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
            >
                {/* Drag Handle - Visible on hover */}
                <div className={`absolute z-50 opacity-0 group-hover:opacity-100 transition-opacity p-2 cursor-move ${item.lineOrientation === 'vertical' ? 'top-2 left-1/2 -translate-x-1/2' : 'left-2 top-1/2 -translate-y-1/2'}`}>
                    <div className="p-1.5 bg-obsidian-900/80 rounded-lg text-gray-400 hover:text-white shadow-lg border border-white/10">
                        <Move className="w-4 h-4" />
                    </div>
                </div>

                {/* Remove Button */}
                <div className={`absolute z-50 opacity-0 group-hover:opacity-100 transition-opacity p-2 ${item.lineOrientation === 'vertical' ? 'bottom-12 left-1/2 -translate-x-1/2' : 'right-2 top-1/2 -translate-y-1/2'}`}>
                    <button
                        onClick={() => onRemove(item.i)}
                        className="p-1.5 bg-red-900/80 rounded-full text-white hover:bg-red-600 shadow-lg"
                        title="Remove Line"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <LineWidget
                    orientation={item.lineOrientation || 'horizontal'}
                    style={item.lineStyle || 'solid'}
                    color={item.lineColor || '#fbbf24'}
                    thickness={item.lineThickness || 2}
                    onUpdateStyle={(updates) => {
                        const newStyle: any = {};
                        if (updates.orientation) newStyle.lineOrientation = updates.orientation;
                        if (updates.style) newStyle.lineStyle = updates.style;
                        if (updates.color) newStyle.lineColor = updates.color;
                        if (updates.thickness) newStyle.lineThickness = updates.thickness;
                        onUpdateStyle(item.i, newStyle);
                    }}
                />
            </div>
        );
    }

    // Standard Widget (Chart/Text)
    const dockedTop = item.annotations?.filter((a: any) => a.dockPosition === 'top') || [];
    const overlays = item.annotations?.filter((a: any) => !a.dockPosition || a.dockPosition === 'overlay') || [];

    // Build border style
    const borderWidth = item.borderWidth || 2; // Default to 2px for bold look
    const borderColor = item.borderColor || 'rgb(255 255 255 / 0.05)';
    const borderStyle = item.borderStyle || 'solid';
    const borderClass = `border-${borderWidth}`;

    return (
        <div
            ref={widgetRef}
            className={`w-full h-full rounded-2xl shadow-lg flex flex-col overflow-hidden group hover:border-neon-gold/30 transition-all duration-300 relative ${!item.backgroundColor && !item.backgroundImage ? (item.backgroundGradient || 'glass-panel') : ''}`}
            style={{
                backgroundColor: item.backgroundColor,
                borderWidth: `${borderWidth}px`,
                borderColor: borderColor,
                borderStyle: borderStyle
            }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
        >
            {/* Background Image Layer */}
            {item.backgroundImage && (
                <div
                    className="absolute inset-0 z-0 pointer-events-none transition-opacity duration-300"
                    style={{
                        backgroundImage: `url(${item.backgroundImage})`,
                        backgroundSize: item.backgroundImageFit || 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        opacity: item.backgroundImageOpacity ?? 1
                    }}
                />
            )}
            {/* Widget Header (visible on hover) */}
            <div className="h-8 flex items-center justify-between px-2 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity absolute top-0 left-0 right-0 z-20 backdrop-blur-sm">
                <div className="flex items-center gap-1">
                    {isDraggable && <Move className="w-3 h-3 text-gray-400 cursor-move" />}
                </div>
                <div className="flex items-center gap-1">
                    {item.type === 'chart' && (
                        <div className="relative">
                            <button
                                onClick={() => setShowExportMenu(!showExportMenu)}
                                className="p-1 hover:bg-white/10 rounded text-gray-400 hover:text-white"
                                title="Export Widget"
                            >
                                <Download className="w-3 h-3" />
                            </button>
                            {showExportMenu && (
                                <div className="absolute right-0 top-full mt-1 w-40 bg-obsidian-900 border border-white/10 rounded-lg shadow-xl overflow-hidden z-50">
                                    <button
                                        onClick={handleExportPNG}
                                        disabled={isExporting}
                                        className="w-full px-3 py-2 text-left text-xs text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2 transition-colors disabled:opacity-50"
                                    >
                                        {isExporting ? <span className="animate-spin">⌛</span> : <Image className="w-3 h-3" />}
                                        {isExporting ? 'Exporting...' : 'Export PNG'}
                                    </button>
                                    <button
                                        onClick={handleExportCSV}
                                        className="w-full px-3 py-2 text-left text-xs text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2 transition-colors"
                                    >
                                        <FileText className="w-3 h-3" /> Export CSV
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    <button onClick={() => onRemove(item.i)} className="p-1 hover:bg-red-500/20 rounded text-gray-400 hover:text-red-400" title="Remove Widget">
                        <X className="w-3 h-3" />
                    </button>

                    {/* Settings Button */}
                    <div className="relative">
                        <button
                            onClick={() => setShowStyleMenu(!showStyleMenu)}
                            className={`p-1 hover:bg-white/10 rounded transition-colors ${showStyleMenu ? 'text-neon-gold bg-white/10' : 'text-gray-400 hover:text-white'}`}
                            title="Widget Settings"
                        >
                            <Settings className="w-3 h-3" />
                        </button>

                        {/* Style Menu Popover */}
                        {showStyleMenu && (
                            <div className="absolute right-0 top-full mt-2 w-64 bg-obsidian-900 border border-gold-500/20 rounded-xl shadow-2xl p-4 z-50 backdrop-blur-md">
                                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                                    <Palette className="w-3 h-3" /> Style
                                </h4>

                                <div className="space-y-4">
                                    {/* Background Image */}
                                    <div className="space-y-2">
                                        <label className="text-xs text-gray-400">Background Image</label>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={item.backgroundImage || ''}
                                                onChange={(e) => onUpdateStyle(item.i, { backgroundImage: e.target.value })}
                                                placeholder="Image URL..."
                                                className="flex-1 bg-obsidian-800 border border-white/10 rounded px-2 py-1 text-xs text-white placeholder-gray-600 focus:border-neon-gold/50 outline-none"
                                            />
                                            {item.backgroundImage && (
                                                <button
                                                    onClick={() => onUpdateStyle(item.i, { backgroundImage: undefined })}
                                                    className="p-1 hover:bg-red-500/20 text-gray-400 hover:text-red-400 rounded"
                                                    title="Clear Image"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Image Options */}
                                    {item.backgroundImage && (
                                        <>
                                            <div className="space-y-2">
                                                <label className="text-xs text-gray-400">Fit Mode</label>
                                                <div className="grid grid-cols-3 gap-1">
                                                    {['cover', 'contain', 'fill'].map((mode) => (
                                                        <button
                                                            key={mode}
                                                            onClick={() => onUpdateStyle(item.i, { backgroundImageFit: mode as any })}
                                                            className={`text-[10px] px-2 py-1 rounded border capitalize ${(item.backgroundImageFit || 'cover') === mode
                                                                ? 'bg-neon-gold/20 border-neon-gold text-neon-gold'
                                                                : 'bg-white/5 border-transparent text-gray-400 hover:bg-white/10'
                                                                }`}
                                                        >
                                                            {mode}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex justify-between text-xs text-gray-400">
                                                    <span>Opacity</span>
                                                    <span>{Math.round((item.backgroundImageOpacity ?? 1) * 100)}%</span>
                                                </div>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="1"
                                                    step="0.1"
                                                    value={item.backgroundImageOpacity ?? 1}
                                                    onChange={(e) => onUpdateStyle(item.i, { backgroundImageOpacity: parseFloat(e.target.value) })}
                                                    className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>



            {/* Docked Content (Top) */}
            {dockedTop.length > 0 && (
                <div className="p-4 pb-0 flex flex-col gap-1">
                    {dockedTop.map((ann: any) => (
                        <div key={ann.id} className="relative group/dock w-full">
                            <TextWidget
                                content={ann.content}
                                fontSize={ann.style?.fontSize === '14px' ? 'medium' : 'large'} // Simplified mapping
                                textColor={ann.style?.color || 'text-gray-200'}
                                isBold={true}
                                onChange={(updates) => {
                                    const updatedAnns = item.annotations?.map((a: any) =>
                                        a.id === ann.id ? { ...a, content: updates.content || a.content, style: { ...a.style, color: updates.textColor } } : a
                                    );
                                    onUpdateStyle(item.i, { annotations: updatedAnns });
                                }}
                            />
                            <button
                                className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover/dock:opacity-100 transition-opacity z-10"
                                onClick={() => {
                                    const updated = item.annotations?.filter((a: any) => a.id !== ann.id);
                                    onUpdateStyle(item.i, { annotations: updated });
                                }}
                            >
                                <X className="w-2 h-2" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Widget Content */}
            <div className="flex-1 overflow-hidden relative p-4">
                {item.type === 'chart' && item.chartId && (
                    <ChartWrapper chartId={item.chartId} allCharts={allCharts} datasets={datasets} relationships={relationships} />
                )}
                {item.type === 'text' && (
                    <TextWidget
                        content={item.content || ''}
                        fontSize={item.fontSize}
                        textAlign={item.textAlign}
                        textColor={item.textColor}
                        isBold={item.isBold}
                        isItalic={item.isItalic}
                        onChange={(updates) => onUpdateStyle(item.i, updates)}
                    />
                )}

                {/* Annotations Layer (Overlays) */}
                {overlays.map((ann: any) => (
                    <div
                        key={ann.id}
                        className="absolute cursor-move group/ann"
                        style={{
                            left: `${ann.x}%`,
                            top: `${ann.y}%`,
                            transform: 'translate(-50%, -50%)',
                            ...ann.style
                        }}
                        onMouseDown={(e) => handleAnnotationDragStart(e, ann.id)}
                    >
                        {ann.content}
                        <button
                            className="absolute -top-3 -right-3 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover/ann:opacity-100 transition-opacity"
                            onClick={(e) => {
                                e.stopPropagation();
                                const updated = item.annotations?.filter((a: any) => a.id !== ann.id);
                                onUpdateStyle(item.i, { annotations: updated });
                            }}
                        >
                            <X className="w-2 h-2" />
                        </button>
                    </div>
                ))}
            </div>

        </div>
    );
};

const DashboardView: React.FC<Pick<WorkspaceProps, 'activeDashboard' | 'allCharts' | 'datasets' | 'relationships' | 'onUpdateDashboardLayout' | 'onUpdateDashboardDetails' | 'onRemoveWidgetFromDashboard' | 'onAddChartToDashboard' | 'onAddTextWidget' | 'onAddLineWidget' | 'onDeleteDashboard'> & {
    allDashboards: Dashboard[];
    onSelectDashboard: (id: string) => void;
    onCreateDashboard: () => void;
}> = ({
    activeDashboard, allCharts, datasets, relationships, onUpdateDashboardLayout, onUpdateDashboardDetails, onRemoveWidgetFromDashboard, onAddChartToDashboard, onAddTextWidget, onAddLineWidget, onDeleteDashboard,
    allDashboards, onSelectDashboard, onCreateDashboard
}) => {
        const [isDraggable, setIsDraggable] = useState(true);
        const [isDraggingColor, setIsDraggingColor] = useState(false);
        const [customColor, setCustomColor] = useState('#ffffff');
        const [isExporting, setIsExporting] = useState(false);

        // Inject Gold Placeholder CSS
        useEffect(() => {
            const style = document.createElement('style');
            style.innerHTML = `
                .react-grid-placeholder {
                    background: rgba(245, 158, 11, 0.2) !important;
                border: 1px dashed #0ea5e9 !important;
                border-radius: 1rem !important;
                opacity: 1 !important;
            }
                body.dragging-color .react-grid-placeholder,
                body.dragging-color .react-grid-item.react-grid-placeholder {
                    display: none !important;
                opacity: 0 !important;
                visibility: hidden !important;
                pointer-events: none !important;
                width: 0 !important;
                height: 0 !important;
            }
                `;
            document.head.appendChild(style);
            return () => { document.head.removeChild(style); };
        }, []);

        // Add/remove body class for color dragging
        useEffect(() => {
            if (isDraggingColor) {
                document.body.classList.add('dragging-color');
            } else {
                document.body.classList.remove('dragging-color');
            }
            return () => {
                document.body.classList.remove('dragging-color');
            };
        }, [isDraggingColor]);

        const onLayoutChange = (layout: any[]) => {
            if (!activeDashboard) return;
            const newLayout = layout.map(l => {
                const existing = activeDashboard.layout.find(i => i.i === l.i);
                return {
                    ...existing!,
                    x: l.x,
                    y: l.y,
                    w: l.w,
                    h: l.h
                };
            });
            onUpdateDashboardLayout(newLayout);
        };

        const handleUpdateWidgetStyle = (id: string, style: Partial<LayoutItem>) => {
            if (!activeDashboard) return;
            const newLayout = activeDashboard.layout.map(item =>
                item.i === id ? { ...item, ...style } : item
            );
            onUpdateDashboardLayout(newLayout);
        };

        const onDrop = (layout: any[], layoutItem: any, _event: Event) => {
            if (!activeDashboard) return;
            const event = _event as DragEvent;
            const type = event.dataTransfer?.getData('type');
            const id = event.dataTransfer?.getData('id');

            if (type === 'chart' && id) {
                onAddChartToDashboard(id, activeDashboard.id, { x: layoutItem.x, y: layoutItem.y });
            } else if (type === 'text-widget') {
                onAddTextWidget && onAddTextWidget(activeDashboard.id, { x: layoutItem.x, y: layoutItem.y, w: 4, h: 2 });
            } else if (type === 'line-widget') {
                onAddLineWidget && onAddLineWidget(activeDashboard.id, 'horizontal', { x: layoutItem.x, y: layoutItem.y });
            }
        };

        // Prevent color drops on the grid itself
        const onGridDragOver = (e: any) => {
            if (e.dataTransfer.types.includes('color') || e.dataTransfer.types.includes('text-dock')) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'none';
                e.stopPropagation();
            }
        };

        // --- Dashboard List View ---
        if (!activeDashboard) {
            return (
                <div className="p-8 h-full overflow-y-auto">
                    <Header title="Dashboards">
                        <button
                            onClick={onCreateDashboard}
                            className="flex items-center gap-2 px-4 py-2 bg-neon-gold/10 text-neon-gold border border-neon-gold/50 rounded-lg hover:bg-neon-gold hover:text-white transition-all shadow-glow"
                        >
                            <Plus className="w-5 h-5" />
                            New Dashboard
                        </button>
                    </Header>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Create New Card */}
                        <button
                            onClick={onCreateDashboard}
                            className="glass-panel p-6 rounded-2xl border border-dashed border-white/10 hover:border-neon-gold/50 flex flex-col items-center justify-center gap-4 group transition-all h-64"
                        >
                            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-neon-gold/20 transition-colors">
                                <Plus className="w-8 h-8 text-gray-400 group-hover:text-neon-gold" />
                            </div>
                            <span className="text-gray-400 group-hover:text-white font-medium">Create New Dashboard</span>
                        </button>

                        {/* Dashboard Cards */}
                        {allDashboards.map(dash => (
                            <div
                                key={dash.id}
                                onClick={() => onSelectDashboard(dash.id)}
                                className="glass-panel p-6 rounded-2xl hover:border-neon-gold/30 transition-all cursor-pointer group relative overflow-hidden h-64 flex flex-col"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-neon-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                                <div className="relative z-10 flex-1">
                                    <h3 className="text-xl font-bold text-white mb-2 group-hover:text-neon-gold transition-colors">{dash.title}</h3>
                                    <p className="text-gray-400 text-sm line-clamp-3">{dash.description || "No description"}</p>
                                </div>

                                <div className="relative z-10 mt-auto flex items-center justify-between text-sm text-gray-500">
                                    <div className="flex items-center gap-2">
                                        <LayoutDashboard className="w-4 h-4" />
                                        <span>{dash.layout.length} Widgets</span>
                                    </div>
                                    <span>{new Date(dash.createdAt).toLocaleDateString()}</span>
                                </div>

                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDeleteDashboard(dash.id);
                                    }}
                                    className="absolute top-4 right-4 p-2 bg-black/20 hover:bg-red-500/20 text-gray-400 hover:text-red-400 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        // --- Active Dashboard View ---
        return (
            <InteractionProvider>
                <ActiveDashboardContent
                    activeDashboard={activeDashboard}
                    allCharts={allCharts}
                    datasets={datasets}
                    relationships={relationships}
                    onUpdateDashboardLayout={onUpdateDashboardLayout}
                    onUpdateDashboardDetails={onUpdateDashboardDetails}
                    onRemoveWidgetFromDashboard={onRemoveWidgetFromDashboard}
                    onDeleteDashboard={onDeleteDashboard}
                    onSelectDashboard={onSelectDashboard}
                    onAddChartToDashboard={onAddChartToDashboard}
                    onAddTextWidget={onAddTextWidget}
                    onAddLineWidget={onAddLineWidget}
                    isDraggable={isDraggable}
                    setIsDraggable={setIsDraggable}
                    isDraggingColor={isDraggingColor}
                    setIsDraggingColor={setIsDraggingColor}
                    customColor={customColor}
                    setCustomColor={setCustomColor}
                    isExporting={isExporting}
                    setIsExporting={setIsExporting}
                />
            </InteractionProvider>
        );
    };

// Inner component to access InteractionContext
const ActiveDashboardContent: React.FC<{
    activeDashboard: Dashboard;
    allCharts: ChartConfig[];
    datasets: Dataset[];
    relationships: DataRelationship[];
    onUpdateDashboardLayout: (layout: LayoutItem[]) => void;
    onUpdateDashboardDetails: (details: Partial<Pick<Dashboard, 'title' | 'description'>>) => void;
    onRemoveWidgetFromDashboard: (id: string) => void;
    onDeleteDashboard: (id: string) => void;
    onSelectDashboard: (id: string) => void;
    onAddChartToDashboard: (chartId: string, dashboardId: string, position?: { x: number, y: number }) => void;
    onAddTextWidget?: (dashboardId: string, position?: { x: number, y: number, w: number, h: number }) => void;
    onAddLineWidget?: (dashboardId: string, orientation: 'horizontal' | 'vertical', position?: { x: number, y: number }) => void;
    isDraggable: boolean;
    setIsDraggable: (val: boolean) => void;
    isDraggingColor: boolean;
    setIsDraggingColor: (val: boolean) => void;
    customColor: string;
    setCustomColor: (val: string) => void;
    isExporting: boolean;
    setIsExporting: (val: boolean) => void;
}> = ({
    activeDashboard, allCharts, datasets, relationships, onUpdateDashboardLayout, onUpdateDashboardDetails,
    onRemoveWidgetFromDashboard, onDeleteDashboard, onSelectDashboard, onAddChartToDashboard, onAddTextWidget, onAddLineWidget,
    isDraggable, setIsDraggable, isDraggingColor, setIsDraggingColor, customColor, setCustomColor, isExporting, setIsExporting
}) => {
        const { activeFilters, clearAllFilters } = useInteraction();

        const onLayoutChange = (layout: any[]) => {
            const newLayout = layout.map(l => {
                const existing = activeDashboard.layout.find(i => i.i === l.i);
                return {
                    ...existing!,
                    x: l.x,
                    y: l.y,
                    w: l.w,
                    h: l.h
                };
            });
            onUpdateDashboardLayout(newLayout);
        };

        const handleUpdateWidgetStyle = (id: string, style: Partial<LayoutItem>) => {
            const newLayout = activeDashboard.layout.map(item =>
                item.i === id ? { ...item, ...style } : item
            );
            onUpdateDashboardLayout(newLayout);
        };

        const onDrop = (layout: any[], layoutItem: any, _event: Event) => {
            const event = _event as DragEvent;
            const type = event.dataTransfer?.getData('type');
            const id = event.dataTransfer?.getData('id');

            if (type === 'chart' && id) {
                onAddChartToDashboard(id, activeDashboard.id, { x: layoutItem.x, y: layoutItem.y });
            } else if (type === 'text-widget') {
                onAddTextWidget && onAddTextWidget(activeDashboard.id, { x: layoutItem.x, y: layoutItem.y, w: 4, h: 2 });
            } else if (type === 'line-widget') {
                onAddLineWidget && onAddLineWidget(activeDashboard.id, 'horizontal', { x: layoutItem.x, y: layoutItem.y });
            }
        };

        const onGridDragOver = (e: any) => {
            if (e.dataTransfer.types.includes('color') || e.dataTransfer.types.includes('text-dock')) {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'none';
                e.stopPropagation();
            }
        };

        return (
            <div className="flex flex-col h-full">
                {/* Custom Header with Editable Title */}
                <header className="flex justify-between items-center p-4 border-b border-white/5 bg-black/20 backdrop-blur-sm z-40">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => onSelectDashboard('')} // Go back to list
                            className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <EditableField
                            value={activeDashboard.title}
                            onSave={(val) => onUpdateDashboardDetails({ title: val })}
                            className="text-2xl font-bold text-gray-200 tracking-tight hover:text-neon-gold cursor-pointer px-2 py-1 -ml-2 rounded-lg hover:bg-white/5 transition-all"
                            inputClassName="text-2xl font-bold bg-transparent border-b-2 border-neon-gold text-gray-200 focus:outline-none px-2"
                            placeholder="Dashboard Title"
                        />
                    </div>

                    {/* Clear Filters Button */}
                    {activeFilters.length > 0 && (
                        <button
                            onClick={clearAllFilters}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-neon-gold/20 text-neon-gold border border-neon-gold/40 hover:bg-neon-gold hover:text-white transition-all text-sm font-medium"
                        >
                            <XCircle className="w-4 h-4" />
                            Clear Filters ({activeFilters.length})
                        </button>
                    )}

                    <div className="flex items-center gap-4">
                        {/* Color Palette */}
                        <div className="flex items-center gap-1 px-3 py-1.5 glass-panel rounded-lg border border-white/10">
                            <Palette className="w-3 h-3 text-gray-400 mr-1" />
                            {BASIC_COLORS.map(color => (
                                <div
                                    key={color.name}
                                    draggable
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('color', color.value);
                                        e.dataTransfer.setData('type', 'color'); // Marker
                                        setIsDraggingColor(true);
                                    }}
                                    onDragEnd={() => setIsDraggingColor(false)}
                                    className="w-4 h-4 rounded-full cursor-grab active:cursor-grabbing border border-white/20 hover:scale-125 transition-transform"
                                    style={{ backgroundColor: color.value }}
                                    title={`Drag to color widget: ${color.name}`}
                                />
                            ))}
                            <div className="w-px h-4 bg-white/10 mx-1" />
                            <div
                                className="relative group w-4 h-4"
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('color', customColor);
                                    e.dataTransfer.setData('type', 'color');
                                    setIsDraggingColor(true);
                                }}
                                onDragEnd={() => setIsDraggingColor(false)}
                            >
                                <input
                                    type="color"
                                    value={customColor}
                                    onChange={(e) => setCustomColor(e.target.value)}
                                    className="w-full h-full rounded cursor-pointer opacity-0 absolute inset-0 z-10"
                                    title="Pick a custom color"
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <div
                                    className="w-full h-full rounded-full border border-white/20 shadow-sm cursor-grab active:cursor-grabbing hover:scale-125 transition-transform"
                                    style={{ backgroundColor: customColor }}
                                    title={`Drag custom color: ${customColor}`}
                                />
                            </div>
                        </div>

                        <div className="h-6 w-px bg-white/10 mx-2" />

                        <button onClick={() => setIsDraggable(!isDraggable)} className={`p-2 rounded-lg transition-colors ${isDraggable ? 'bg-neon-gold text-white shadow-[0_0_15px_rgba(245,158,11,0.3)]' : 'bg-white/10 text-gray-400'}`} title="Toggle Edit Mode">
                            <Move className="w-4 h-4" />
                        </button>
                        <button onClick={() => onDeleteDashboard(activeDashboard.id)} className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors" title="Delete Dashboard">
                            <Trash2 className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => {
                                // Save dashboard logic - for now just show a visual feedback
                                console.log('Dashboard saved:', activeDashboard);
                            }}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-neon-gold text-white font-bold hover:bg-neon-gold/80 transition-colors shadow-[0_0_15px_rgba(245,158,11,0.3)]"
                            title="Save Dashboard"
                        >
                            <Save className="w-4 h-4" />
                            Save
                        </button>
                        <button
                            onClick={async () => {
                                const dashboardElement = document.querySelector('.layout') as HTMLElement;
                                if (dashboardElement) {
                                    setIsExporting(true);
                                    try {
                                        await exportElementAsPNG(dashboardElement, `dashboard-${activeDashboard.title || 'export'}`);
                                    } catch (err) {
                                        console.error(err);
                                        alert("Export failed");
                                    } finally {
                                        setIsExporting(false);
                                    }
                                }
                            }}
                            disabled={isExporting}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white border border-white/10 transition-colors disabled:opacity-50"
                            title="Export Dashboard as PNG"
                        >
                            {isExporting ? <span className="animate-spin">⌛</span> : <Image className="w-4 h-4" />}
                            {isExporting ? 'Exporting...' : 'Export'}
                        </button>
                    </div>
                </header>

                <div className="flex-1 overflow-hidden flex">
                    {/* Widget Palette - Restored */}
                    {isDraggable && (
                        <div className="w-56 glass-panel border-r border-white/10 p-4 flex flex-col gap-4 overflow-y-auto backdrop-blur-sm z-30">
                            <div>
                                <h4 className="text-xs font-bold text-neon-gold/70 uppercase tracking-widest mb-3">Widgets</h4>
                                <div className="space-y-2">
                                    <div
                                        draggable
                                        onDragStart={(e) => e.dataTransfer.setData('type', 'text-widget')}
                                        className="flex items-center gap-3 px-3 py-2.5 bg-white/5 hover:bg-white/10 rounded-xl text-sm text-gray-300 border border-white/10 transition-colors cursor-grab active:cursor-grabbing"
                                    >
                                        <Type className="w-4 h-4 text-neon-gold" /> Add Text
                                    </div>
                                    <div
                                        draggable
                                        onDragStart={(e) => e.dataTransfer.setData('type', 'line-widget')}
                                        className="flex items-center gap-3 px-3 py-2.5 bg-white/5 hover:bg-white/10 rounded-xl text-sm text-gray-300 border border-white/10 transition-colors cursor-grab active:cursor-grabbing"
                                    >
                                        <Minus className="w-4 h-4 text-neon-gold" /> Add Line
                                    </div>
                                </div>
                            </div>

                            <div>
                                <h4 className="text-xs font-bold text-neon-gold/70 uppercase tracking-widest mb-3">Saved Charts</h4>
                                <div className="space-y-2">
                                    {allCharts.length === 0 ? (
                                        <p className="text-xs text-gray-500 italic">No saved charts yet.</p>
                                    ) : (
                                        allCharts.map(chart => (
                                            <DraggableSidebarItem
                                                key={chart.id}
                                                icon={<BarChart2 />}
                                                label={chart.title}
                                                onDragStart={(e) => {
                                                    e.dataTransfer.setData('type', 'chart');
                                                    e.dataTransfer.setData('id', chart.id);
                                                }}
                                            />
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Grid Canvas */}
                    <div className="flex-1 overflow-y-auto bg-transparent p-4 relative custom-scrollbar" onDragOver={onGridDragOver}>
                        {activeDashboard.layout.length === 0 && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 pointer-events-none">
                                <div className="w-24 h-24 border-2 border-dashed border-gray-700 rounded-xl mb-4 flex items-center justify-center">
                                    <Plus className="w-8 h-8 opacity-50" />
                                </div>
                                <p>Drag widgets here to build your dashboard</p>
                            </div>
                        )}

                        <ResponsiveGridLayout
                            className={`layout min-h-full ${isDraggingColor ? 'dragging-color' : ''}`}
                            layouts={{ lg: activeDashboard.layout }}
                            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                            rowHeight={60}
                            onLayoutChange={onLayoutChange}
                            isDraggable={isDraggable}
                            isResizable={isDraggable}
                            onDrop={onDrop}
                            isDroppable={!isDraggingColor}
                            droppingItem={{ i: 'dropping', w: 4, h: 4 }}
                            draggableHandle=".cursor-move"
                            margin={[16, 16]}
                            resizeHandles={['s', 'w', 'e', 'n', 'sw', 'se', 'ne']}
                        >
                            {activeDashboard.layout.map(item => (
                                <div key={item.i} data-grid={item}>
                                    <DashboardWidget
                                        item={item}
                                        allCharts={allCharts}
                                        datasets={datasets}
                                        relationships={relationships}
                                        onRemove={onRemoveWidgetFromDashboard}
                                        onUpdateStyle={handleUpdateWidgetStyle}
                                        isDraggable={isDraggable}
                                    />
                                </div>
                            ))}
                        </ResponsiveGridLayout>
                    </div>
                </div>
                <style>{`
                .react-resizable-handle {
                    width: 20px !important;
                    height: 20px !important;
                    background-color: transparent;
                    border: 1px solid transparent;
                    border-radius: 50%;
                    bottom: 5px !important;
                    right: 5px !important;
                    z-index: 50;
                    transition: all 0.2s;
                    opacity: 0;
                }
                .react-grid-item:hover .react-resizable-handle,
                .react-grid-item.react-dragging .react-resizable-handle {
                    opacity: 1;
                    background-color: rgba(255, 255, 255, 0.1);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                }
                .react-resizable-handle:hover {
                    background-color: #f59e0b !important; /* Gold */
                    transform: scale(1.2);
                }
                .react-resizable-handle::after {
                    content: '';
                    display: block;
                    width: 6px;
                    height: 6px;
                    border-right: 2px solid white;
                    border-bottom: 2px solid white;
                    position: absolute;
                    top: 5px;
                    left: 5px;
                }
            `}</style>
            </div>
        );
    };

export default DashboardView;
